import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash, Copy, ArrowSquareOut, LockKey, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import { useAuth } from "@/context/AuthContext";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"]; let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    fetchList();
     
  }, [user, authLoading]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transfers");
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load transfers");
    } finally {
      setLoading(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this transfer?")) return;
    try {
      await api.delete(`/transfers/${id}`);
      setItems((it) => it.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (authLoading) return <PageShell title="Loading…" />;

  return (
    <PageShell
      eyebrow={`Welcome, ${user?.name || "you"}`}
      title="Your transfers."
      subtitle="A history of everything you’ve sent. Codes, sizes, expiry — at a glance."
    >
      <div className="flex items-center justify-between mb-5">
        <div className="small-caps" data-testid="transfers-count">{items.length} transfer{items.length === 1 ? "" : "s"}</div>
        <Link to="/" className="btn-brut-primary" data-testid="new-transfer-btn">+ New transfer</Link>
      </div>

      {loading ? (
        <div className="border border-dashed border-black/20 p-12 text-center text-black/50">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-black/20 p-16 text-center" data-testid="empty-state">
          <div className="text-2xl font-bold mb-2">No transfers yet</div>
          <div className="text-black/60 mb-6">Send your first file to see it here.</div>
          <Link to="/" className="btn-brut-primary">Start sending</Link>
        </div>
      ) : (
        <div className="overflow-x-auto border border-black" data-testid="transfers-table">
          <table className="w-full text-sm">
            <thead className="bg-black/5">
              <tr className="text-left">
                <th className="px-4 py-3 small-caps">Code</th>
                <th className="px-4 py-3 small-caps">Files</th>
                <th className="px-4 py-3 small-caps">Size</th>
                <th className="px-4 py-3 small-caps">Downloads</th>
                <th className="px-4 py-3 small-caps">Expires</th>
                <th className="px-4 py-3 small-caps">Status</th>
                <th className="px-4 py-3 small-caps text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/10">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-black/[0.03]" data-testid={`row-${t.code}`}>
                  <td className="px-4 py-3 font-mono text-[#FF3366] tracking-widest">{t.code}</td>
                  <td className="px-4 py-3">{t.file_count}</td>
                  <td className="px-4 py-3 font-mono">{fmtBytes(t.total_size)}</td>
                  <td className="px-4 py-3 font-mono">{t.downloads}</td>
                  <td className="px-4 py-3 font-mono text-black/70">
                    <Clock size={12} className="inline mr-1"/>{new Date(t.expires_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    {t.is_expired ? (
                      <span className="text-black/40 font-mono text-xs uppercase">Expired</span>
                    ) : (
                      <span className="text-[#10B981] font-mono text-xs uppercase">Active</span>
                    )}
                    {t.has_password && <LockKey size={12} className="inline ml-2 text-[#FF3366]" />}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => copy(t.code)} title="Copy code" className="p-2 border border-black/15 hover:border-black" data-testid={`copy-${t.code}`}>
                        <Copy size={14}/>
                      </button>
                      <Link to={`/r/${t.code}`} title="Open" className="p-2 border border-black/15 hover:border-black" data-testid={`open-${t.code}`}>
                        <ArrowSquareOut size={14}/>
                      </Link>
                      <button onClick={() => del(t.id)} title="Delete" className="p-2 border border-black/15 hover:border-[#FF3366] hover:text-[#FF3366]" data-testid={`delete-${t.code}`}>
                        <Trash size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
